<template>
  <v-container fluid>
    <v-tabs
      v-model="subTab"
    >
      <v-tab value="charts" text="Charts" />
      <v-tab value="spatial" text="Spatial" />
    </v-tabs>
    <div class="d-flex align-center mt-2">
      <v-switch
        v-model="onEffortOnly"
        label="On-effort only"
        color="primary"
        density="compact"
        hide-details
        class="flex-grow-0"
      />
      <v-tooltip
        location="bottom"
        text="Restrict charts and the spatial map to sightings recorded during active survey effort. Records without an effort value are treated as off-effort and excluded."
      >
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            size="small"
            class="ms-2 text-medium-emphasis"
          >
            mdi-information-outline
          </v-icon>
        </template>
      </v-tooltip>
    </div>
    <div
      v-show="subTab === 'charts' && loading"
      class="py-4 text-medium-emphasis"
    >
      Loading analytics&hellip;
    </div>
    <v-row v-show="subTab === 'charts' && !loading">
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          variant="outlined"
          class="chart-card"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Species Composition
          </v-card-title>
          <v-card-text>
            <div class="chart-wrap">
              <canvas
                ref="speciesCanvas"
                role="img"
                aria-label="Doughnut chart of sightings by species group"
              >Sightings by species group.</canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          variant="outlined"
          class="chart-card"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Sightings per Year by Species
          </v-card-title>
          <v-card-text>
            <div class="chart-wrap">
              <canvas
                ref="yearCanvas"
                role="img"
                aria-label="Stacked bar chart of sightings per year by species group"
              >Sightings per year, stacked by species group.</canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          variant="outlined"
          class="chart-card"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Land vs Marine
          </v-card-title>
          <v-card-text>
            <div class="chart-wrap">
              <canvas
                ref="typeCanvas"
                role="img"
                aria-label="Doughnut chart of sightings by sighting type"
              >Sightings by type.</canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          variant="outlined"
          class="chart-card"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Orca Ecotype
          </v-card-title>
          <v-card-text>
            <div class="chart-wrap">
              <canvas
                ref="ecotypeCanvas"
                role="img"
                aria-label="Doughnut chart of orca sightings by ecotype"
              >Orca sightings by ecotype.</canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          variant="outlined"
          class="chart-card"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Behaviour Frequency
          </v-card-title>
          <v-card-text>
            <div class="chart-wrap-tall">
              <canvas
                ref="behaviourCanvas"
                role="img"
                aria-label="Horizontal bar chart of most frequent behaviours"
              >Most frequent behaviours.</canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          variant="outlined"
          class="chart-card"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Orca Clan / Pod
          </v-card-title>
          <v-card-text>
            <div class="chart-wrap-tall">
              <canvas
                ref="podCanvas"
                role="img"
                aria-label="Horizontal bar chart of orca sightings by clan and pod"
              >Orca sightings by clan and pod.</canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card
          variant="outlined"
          class="chart-card"
        >
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Whales by First Year Sighted
          </v-card-title>
          <v-card-text>
            <div class="chart-wrap">
              <canvas
                ref="firstYearCanvas"
                role="img"
                aria-label="Bar chart of catalogued whales by the year they were first sighted"
              >Catalogued whales by first year sighted.</canvas>
            </div>
            <v-alert
              v-if="unregisteredCount > 0"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              {{ unregisteredCount }} whale ID(s) appear in sightings but aren't in the catalogue, so they're not counted here.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <div v-show="subTab === 'spatial'">
      <v-card
        variant="outlined"
        class="chart-card"
      >
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Sighting Density
        </v-card-title>
        <v-card-text>
          <div
            v-if="spatialLoading"
            class="py-4 text-medium-emphasis"
          >
            Loading map&hellip;
          </div>
          <div
            ref="spatialMapEl"
            class="spatial-map"
          />
        </v-card-text>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Chart from "chart.js/auto";
import speciesHelper from "@/helpers/speciesBuckets";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const NAVY = "#364563";
const FALLBACK = "#979797";
const TYPE_COLORS = { Marine: "#1E6091", Land: "#8D6E63", Unknown: FALLBACK };
const ECOTYPE_COLORS = { Resident: "#1D9E75", Transient: "#D85A30", Offshore: "#534AB7" };
const BEHAVIOUR_TOP = 10;
const POD_TOP = 12;

export default {
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      subTab: "charts",
      onEffortOnly: false,
      spatialMap: null,
      spatialInit: false,
      speciesChart: null,
      yearChart: null,
      typeChart: null,
      ecotypeChart: null,
      behaviourChart: null,
      podChart: null,
      firstYearChart: null,
    };
  },
  computed: {
    ...mapGetters({
      loading: "analytics/loading",
      summary: "analytics/summary",
      spatial: "analytics/spatial",
      spatialLoading: "analytics/spatialLoading",
      unregisteredWhales: "whales/unregisteredWhales",
    }),
    unregisteredCount() {
      return (this.unregisteredWhales || []).length;
    },
    effectiveFilters() {
      return { ...this.filters, onEffort: this.onEffortOnly };
    },
  },
  watch: {
    summary() {
      this.renderCharts();
    },
    subTab(value) {
      if (value !== "spatial") return;
      if (this.spatialMap) {
        this.$nextTick(() => this.spatialMap.resize());
      } else {
        this.initSpatial();
      }
    },
    effectiveFilters: {
      deep: true,
      handler() {
        this.loadSummary(this.effectiveFilters);
        if (this.spatialMap) {
          this.loadSpatial(this.effectiveFilters).then(() => this.refreshHeatmap());
        }
      },
    },
  },
  async created() {
    this.loadUnregisteredWhales();
    await this.loadSummary(this.effectiveFilters);
  },
  beforeUnmount() {
    this.destroyCharts();
    if (this.spatialMap) {
      this.spatialMap.remove();
      this.spatialMap = null;
    }
  },
  methods: {
    ...mapActions({
      loadSummary: "analytics/loadSummary",
      loadSpatial: "analytics/loadSpatial",
      loadUnregisteredWhales: "whales/loadUnregisteredWhales",
    }),
    async initSpatial() {
      if (this.spatialInit) return;
      this.spatialInit = true;
      await this.loadSpatial(this.effectiveFilters);
      this.$nextTick(() => this.createSpatialMap());
    },
    createSpatialMap() {
      const el = this.$refs.spatialMapEl;
      if (!el || this.spatialMap) return;
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
      this.spatialMap = new mapboxgl.Map({
        container: el,
        style: "mapbox://styles/mapbox/outdoors-v12",
        center: [-128.9110, 54.6057],
        zoom: 4.15,
      });
      this.spatialMap.addControl(new mapboxgl.NavigationControl());
      this.spatialMap.on("load", () => this.addHeatmapLayer());
    },
    buildSpatialFeatures() {
      return (this.spatial?.points || []).map((point) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [point.lon, point.lat] },
        properties: {},
      }));
    },
    refreshHeatmap() {
      if (!this.spatialMap) return;
      const source = this.spatialMap.getSource("sightings");
      if (!source) return;
      source.setData({ type: "FeatureCollection", features: this.buildSpatialFeatures() });
    },
    addHeatmapLayer() {
      if (!this.spatialMap) return;
      const features = this.buildSpatialFeatures();
      this.spatialMap.addSource("sightings", {
        type: "geojson",
        data: { type: "FeatureCollection", features },
      });
      this.spatialMap.addLayer({
        id: "sightings-heat",
        type: "heatmap",
        source: "sightings",
        paint: {
          "heatmap-radius": 18,
          "heatmap-intensity": 1,
          "heatmap-opacity": 0.85,
          "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0, "rgba(33,102,172,0)",
            0.2, "rgb(103,169,207)",
            0.4, "rgb(209,229,240)",
            0.6, "rgb(253,219,199)",
            0.8, "rgb(239,138,98)",
            1, "rgb(178,24,43)",
          ],
        },
      });
    },
    destroyCharts() {
      [
        "speciesChart",
        "yearChart",
        "typeChart",
        "ecotypeChart",
        "behaviourChart",
        "podChart",
        "firstYearChart",
      ].forEach((key) => {
        if (this[key]) {
          this[key].destroy();
          this[key] = null;
        }
      });
    },
    renderCharts() {
      this.$nextTick(() => {
        this.destroyCharts();
        this.renderSpeciesChart();
        this.renderYearChart();
        this.renderTypeChart();
        this.renderEcotypeChart();
        this.renderBehaviourChart();
        this.renderPodChart();
        this.renderFirstYearChart();
      });
    },
    buildDonut(canvas, labels, data, colors) {
      return new Chart(canvas, {
        type: "doughnut",
        data: {
          labels,
          datasets: [{ data, backgroundColor: colors, borderWidth: 1 }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "right", labels: { boxWidth: 12, font: { size: 12 } } },
          },
        },
      });
    },
    buildBar(canvas, labels, data, color, horizontal) {
      return new Chart(canvas, {
        type: "bar",
        data: { labels, datasets: [{ data, backgroundColor: color }] },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: horizontal ? "y" : "x",
          plugins: { legend: { display: false } },
          scales: horizontal
            ? { x: { ticks: { precision: 0 } } }
            : { x: { ticks: { autoSkip: false } }, y: { ticks: { precision: 0 } } },
        },
      });
    },
    renderSpeciesChart() {
      const canvas = this.$refs.speciesCanvas;
      if (!canvas) return;
      const buckets = speciesHelper.bucketTotals(this.summary?.speciesCounts);
      this.speciesChart = this.buildDonut(
        canvas,
        buckets.map((bucket) => bucket.label),
        buckets.map((bucket) => bucket.count),
        buckets.map((bucket) => bucket.color)
      );
    },
    renderYearChart() {
      const canvas = this.$refs.yearCanvas;
      if (!canvas) return;
      const { years, series } = speciesHelper.bucketYearSeries(this.summary?.sightingsByYearSpecies);
      this.yearChart = new Chart(canvas, {
        type: "bar",
        data: {
          labels: years.map((year) => String(year)),
          datasets: series.map((entry) => ({
            label: entry.label,
            data: entry.data,
            backgroundColor: entry.color,
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { stacked: true, ticks: { autoSkip: false } },
            y: { stacked: true, ticks: { precision: 0 } },
          },
          plugins: {
            legend: { position: "top", labels: { boxWidth: 12, font: { size: 12 } } },
          },
        },
      });
    },
    renderTypeChart() {
      const canvas = this.$refs.typeCanvas;
      if (!canvas) return;
      const counts = this.summary?.sightingTypeCounts || [];
      this.typeChart = this.buildDonut(
        canvas,
        counts.map((entry) => entry.key),
        counts.map((entry) => entry.count),
        counts.map((entry) => TYPE_COLORS[entry.key] || FALLBACK)
      );
    },
    renderEcotypeChart() {
      const canvas = this.$refs.ecotypeCanvas;
      if (!canvas) return;
      const counts = this.summary?.orcaEcotypeCounts || [];
      this.ecotypeChart = this.buildDonut(
        canvas,
        counts.map((entry) => entry.key),
        counts.map((entry) => entry.count),
        counts.map((entry) => ECOTYPE_COLORS[entry.key] || FALLBACK)
      );
    },
    renderBehaviourChart() {
      const canvas = this.$refs.behaviourCanvas;
      if (!canvas) return;
      const counts = this.summary?.behaviourCounts || [];
      const top = counts.slice(0, BEHAVIOUR_TOP);
      const restTotal = counts.slice(BEHAVIOUR_TOP).reduce((sum, entry) => sum + entry.count, 0);
      const labels = top.map((entry) => entry.key);
      const data = top.map((entry) => entry.count);
      if (restTotal > 0) {
        labels.push("Other");
        data.push(restTotal);
      }
      this.behaviourChart = this.buildBar(canvas, labels, data, NAVY, true);
    },
    renderPodChart() {
      const canvas = this.$refs.podCanvas;
      if (!canvas) return;
      const rows = (this.summary?.orcaPodCounts || [])
        .filter((entry) => entry.count > 0)
        .slice()
        .sort((a, b) => b.count - a.count)
        .slice(0, POD_TOP);
      const labels = rows.map((entry) => `${entry.clan || "Unknown"} / ${entry.pod || "Unknown"}`);
      const data = rows.map((entry) => entry.count);
      this.podChart = this.buildBar(canvas, labels, data, NAVY, true);
    },
    renderFirstYearChart() {
      const canvas = this.$refs.firstYearCanvas;
      if (!canvas) return;
      const rows = this.summary?.whalesByFirstYear || [];
      const labels = rows.map((entry) => String(entry.year));
      const counts = rows.map((entry) => entry.count);
      let running = 0;
      const cumulative = counts.map((count) => (running += count));
      this.firstYearChart = new Chart(canvas, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              type: "bar",
              label: "New whales",
              data: counts,
              backgroundColor: NAVY,
              order: 2,
            },
            {
              type: "line",
              label: "Cumulative",
              data: cumulative,
              borderColor: "#0D9488",
              backgroundColor: "#0D9488",
              tension: 0.3,
              pointRadius: 2,
              order: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ticks: { autoSkip: false } },
            y: { ticks: { precision: 0 } },
          },
          plugins: {
            legend: { position: "top", labels: { boxWidth: 12, font: { size: 12 } } },
          },
        },
      });
    },
  },
};
</script>

<style>
.chart-card {
  height: 100%;
}
.chart-wrap {
  position: relative;
  height: 320px;
  width: 100%;
}
.chart-wrap-tall {
  position: relative;
  height: 460px;
  width: 100%;
}
.spatial-map {
  width: 100%;
  height: 67vh;
  min-height: 400px;
}
</style>
